import { type Static, Type as t } from "@sinclair/typebox";
import { DownloadErrorCodeSchema } from "../errors";
import { GalleryCard, GalleryDetail } from "./gallery";
import {
  AssignablePolicyLevelSchema,
  BoolParam,
  CategorySchema,
  CursorQuery,
  closed,
  DownloadQualitySchema,
  DownloadRouteSchema,
  DownloadRunnerSchema,
  DownloadStageSchema,
  DownloadStatusSchema,
  Empty,
  floatParam,
  GidParam,
  IdParam,
  IsoDateTime,
  LimitParam,
  ListParam,
  literals,
  nullable,
  PolicyLevelSchema,
  pageOf,
  RequestedRouteSchema,
  RoleSchema,
  SyncFinishReason,
  SyncStreamSchema,
  Tag,
  Uuid,
  VerdictSchema,
} from "./primitives";
import { route, withIds } from "./route";

const GidParams = t.Object({ gid: GidParam });

// ======================================================================================
// auth  (/api/auth)
// ======================================================================================

export const User = closed({
  id: t.Integer(),
  username: t.String(),
  role: RoleSchema,
  avatarUrl: nullable(t.String()),
  createdAt: IsoDateTime,
});
export type UserType = Static<typeof User>;

export const AuthSession = closed({
  accessToken: t.String({ description: "JWT (HS256), sent as `Authorization: Bearer`" }),
  expiresIn: t.Integer({ description: "seconds until accessToken expires" }),
  user: User,
});
export type AuthSessionType = Static<typeof AuthSession>;

export const DISCORD_ACCESS_MESSAGES = {
  not_member: "지정된 Discord 서버의 멤버가 아닙니다",
  missing_role: "필요한 역할이 없습니다. 서버 관리자에게 문의하세요",
  unavailable: "Discord 인증을 확인할 수 없습니다. 잠시 후 다시 시도하세요",
} as const;

export const authRoutes = withIds({
  "auth.discordStart": route({
    module: "auth",
    method: "GET",
    path: "/api/auth/discord/start",
    access: "public",
    summary: "Discord OAuth 시작: 서명된 state 쿠키를 심고 Discord로 리다이렉트",
    query: t.Object({ returnTo: t.Optional(t.String({ pattern: "^/[^/\\\\]", maxLength: 512 })) }),
    responses: { 302: t.Null() },
    errors: [],
    notes: "Discord로 302. OAuth state는 서명된 httpOnly 쿠키에 두고 서버 메모리에는 두지 않는다.",
  }),
  "auth.discordCallback": route({
    module: "auth",
    method: "GET",
    path: "/api/auth/discord/callback",
    access: "public",
    summary: "Discord가 돌아오는 곳. 성공하면 리프레시 쿠키를 심고 SPA로 리다이렉트",
    query: t.Object({ code: t.String({ minLength: 1 }), state: t.String({ minLength: 1 }) }),
    responses: { 302: t.Null() },
    errors: ["INVALID_CREDENTIALS", "ACCOUNT_DISABLED"],
    notes:
      "state가 다르면 INVALID_CREDENTIALS. 길드/역할 거절 또는 Discord 장애는 /login?discordError=not_member|missing_role|unavailable로 302. 관리자 ID는 길드/역할 검사를 생략. 통과 뒤에만 계정·세션 생성.",
  }),
  "auth.keyLogin": route({
    module: "auth",
    method: "POST",
    path: "/api/auth/key-login",
    access: "public",
    summary: "접근 키로 로그인(역할 guest)",
    body: t.Object({ key: t.String({ minLength: 8, maxLength: 256 }) }),
    responses: { 200: AuthSession },
    errors: ["INVALID_CREDENTIALS", "ACCOUNT_DISABLED", "RATE_LIMITED"],
    notes: "리프레시 쿠키와 CloudFront 서명 쿠키를 심는다.",
  }),
  "auth.refresh": route({
    module: "auth",
    method: "POST",
    path: "/api/auth/refresh",
    access: "public",
    summary: "리프레시 쿠키를 회전하고 새 액세스 토큰을 발급",
    responses: { 200: AuthSession },
    errors: ["TOKEN_EXPIRED", "ACCOUNT_DISABLED", "FORBIDDEN", "UNAVAILABLE"],
    notes:
      "재사용은 계열 전체 폐기. Discord 세션은 길드/역할 재검사(관리자 ID 제외); 탈퇴·역할 상실은 FORBIDDEN과 계열 폐기. Discord 장애는 UNAVAILABLE, 기존 토큰·쿠키 보존. 접근 키 세션은 검사 제외.",
  }),
  "auth.logout": route({
    module: "auth",
    method: "POST",
    path: "/api/auth/logout",
    access: "public",
    summary: "리프레시 토큰을 폐기하고 리프레시·CloudFront 쿠키를 지움",
    responses: { 204: Empty },
    errors: [],
  }),
  "auth.me": route({
    module: "auth",
    method: "GET",
    path: "/api/auth/me",
    access: "user",
    summary: "로그인한 계정",
    responses: { 200: closed({ user: User }) },
    errors: [],
  }),
});

// ======================================================================================
// users  (/api/users)
// ======================================================================================

const userSettingsProps = {
  hideBlockedCards: t.Boolean({ description: "R-4: hide blocked cards from search results (default false)" }),
  readerReverseNavigation: t.Boolean({
    description: "Reverse desktop reader left/right page navigation (default false)",
  }),
  readerReverseNavigationMobile: t.Boolean({
    description: "Reverse mobile reader left/right page navigation (default false)",
  }),
};
export const UserSettings = closed(userSettingsProps);
export type UserSettingsType = Static<typeof UserSettings>;

export const usersRoutes = withIds({
  "users.getSettings": route({
    module: "users",
    method: "GET",
    path: "/api/users/me/settings",
    access: "user",
    summary: "내 설정",
    responses: { 200: UserSettings },
    errors: [],
  }),
  "users.patchSettings": route({
    module: "users",
    method: "PATCH",
    path: "/api/users/me/settings",
    access: "user",
    summary: "내 설정 변경",
    body: t.Partial(t.Object(userSettingsProps)),
    responses: { 200: UserSettings },
    errors: [],
  }),
});

// ======================================================================================
// personal  (/api/favorites, /api/history)
// ======================================================================================

export const FavoriteItem = closed({ addedAt: IsoDateTime, gallery: GalleryCard });
export const HistoryItem = closed({
  viewedAt: IsoDateTime,
  viewCount: t.Integer({ minimum: 1 }),
  gallery: GalleryCard,
});

export const personalRoutes = withIds({
  "personal.listFavorites": route({
    module: "personal",
    method: "GET",
    path: "/api/favorites",
    access: "user",
    summary: "내 즐겨찾기, 최신순",
    query: CursorQuery,
    responses: { 200: pageOf(FavoriteItem) },
    errors: [],
  }),
  "personal.addFavorite": route({
    module: "personal",
    method: "PUT",
    path: "/api/favorites/:gid",
    access: "user",
    summary: "즐겨찾기 추가(멱등)",
    params: GidParams,
    responses: { 204: Empty },
    errors: ["NOT_FOUND"],
  }),
  "personal.removeFavorite": route({
    module: "personal",
    method: "DELETE",
    path: "/api/favorites/:gid",
    access: "user",
    summary: "즐겨찾기 삭제(멱등)",
    params: GidParams,
    responses: { 204: Empty },
    errors: [],
  }),
  "personal.listHistory": route({
    module: "personal",
    method: "GET",
    path: "/api/history",
    access: "user",
    summary: "내 열람 기록, 최근순",
    query: CursorQuery,
    responses: { 200: pageOf(HistoryItem) },
    errors: [],
  }),
  "personal.recordHistory": route({
    module: "personal",
    method: "POST",
    path: "/api/history",
    access: "user",
    summary: "갤러리를 열었음을 기록(업서트, viewCount 증가)",
    body: t.Object({ gid: t.Integer({ minimum: 1 }) }),
    responses: { 204: Empty },
    errors: ["NOT_FOUND"],
  }),
  "personal.removeHistory": route({
    module: "personal",
    method: "DELETE",
    path: "/api/history/:gid",
    access: "user",
    summary: "기록에서 갤러리 하나 삭제",
    params: GidParams,
    responses: { 204: Empty },
    errors: [],
  }),
  "personal.clearHistory": route({
    module: "personal",
    method: "DELETE",
    path: "/api/history",
    access: "user",
    summary: "기록 전체 삭제",
    responses: { 204: Empty },
    errors: [],
  }),
});

// ======================================================================================
// catalog  (/api/galleries, /api/tags, /api/categories)
// ======================================================================================

export const GALLERY_SORTS = ["gid_desc", "gid_asc", "posted_desc", "posted_asc"] as const;
export type GallerySort = (typeof GALLERY_SORTS)[number];

/** Filters shared by the list and count endpoints. Arrays use repeated keys: `?tag=a&tag=b`. */
const GalleryFilter = {
  gid: t.Optional(GidParam),
  q: t.Optional(t.String({ minLength: 1, maxLength: 200, description: "title substring (trigram)" })),
  tag: t.Optional(ListParam("galleries must have ALL of these tags (`namespace:name`)")),
  xtag: t.Optional(ListParam("galleries must have NONE of these tags")),
  category: t.Optional(ListParam("any of these categories")),
  state: t.Optional(ListParam("any of these states; default listed and expunged")),
  ratingMin: t.Optional(floatParam("^[0-5](\\.[0-9]{1,2})?$", "minimum average rating, 0..5")),
  blocked: t.Optional(
    t.Union([t.Literal("show"), t.Literal("hide")], {
      description: "override the user's hideBlockedCards setting for this request",
    }),
  ),
};

export const GalleryListQuery = t.Object({
  ...GalleryFilter,
  sort: t.Optional(literals(GALLERY_SORTS)),
  cursor: t.Optional(
    t.String({ minLength: 1, maxLength: 512, description: "opaque; pass back nextCursor as is" }),
  ),
  limit: t.Optional(LimitParam),
});
export const GalleryCountQuery = t.Object(GalleryFilter);

export const TagCount = closed({
  tag: Tag,
  namespace: t.String(),
  count: t.Integer({ minimum: 0 }),
  policy: PolicyLevelSchema,
});

export const catalogRoutes = withIds({
  "catalog.list": route({
    module: "catalog",
    method: "GET",
    path: "/api/galleries",
    access: "user",
    summary: "갤러리 검색(키셋 페이지, 전체 개수 없음)",
    query: GalleryListQuery,
    responses: { 200: pageOf(GalleryCard) },
    errors: [],
    notes:
      "메타데이터를 받은 행만 나온다. 차단 갤러리는 사용자 설정으로 숨기지 않는 한 썸네일 없는 카드로 나온다.",
  }),
  "catalog.count": route({
    module: "catalog",
    method: "GET",
    path: "/api/galleries/count",
    access: "user",
    summary: "필터에 맞는 갤러리 수(캐시된 값)",
    query: GalleryCountQuery,
    responses: {
      200: closed({ count: t.Integer({ minimum: 0 }), exact: t.Boolean(), cachedAt: IsoDateTime }),
    },
    errors: [],
  }),
  "catalog.get": route({
    module: "catalog",
    method: "GET",
    path: "/api/galleries/:gid",
    access: "user",
    summary: "갤러리 하나",
    params: GidParams,
    responses: { 200: GalleryDetail },
    errors: ["NOT_FOUND"],
  }),
  "catalog.suggestTags": route({
    module: "catalog",
    method: "GET",
    path: "/api/tags/suggest",
    access: "user",
    summary: "태그 자동완성",
    query: t.Object({
      q: t.String({ minLength: 1, maxLength: 100 }),
      limit: t.Optional(LimitParam),
    }),
    responses: { 200: closed({ items: t.Array(TagCount) }) },
    errors: [],
  }),
  "catalog.popularTags": route({
    module: "catalog",
    method: "GET",
    path: "/api/tags/popular",
    access: "user",
    summary: "많이 쓰인 태그(네임스페이스 하나로 좁힐 수 있음)",
    query: t.Object({
      namespace: t.Optional(t.String({ minLength: 1, maxLength: 50 })),
      limit: t.Optional(LimitParam),
    }),
    responses: { 200: closed({ items: t.Array(TagCount) }) },
    errors: [],
  }),
  "catalog.categoryCounts": route({
    module: "catalog",
    method: "GET",
    path: "/api/categories/counts",
    access: "user",
    summary: "카테고리별 갤러리 수(캐시된 값)",
    responses: {
      200: closed({
        items: t.Array(closed({ category: CategorySchema, count: t.Integer({ minimum: 0 }) })),
        cachedAt: IsoDateTime,
      }),
    },
    errors: [],
  }),
});

// ======================================================================================
// downloads  (/api/downloads)
// ======================================================================================

import { DownloadOrigin } from "./bot";

export const DownloadJob = closed({
  id: Uuid,
  gid: t.Integer({ minimum: 1 }),
  galleryTitle: nullable(t.String()),
  galleryPolicy: nullable(PolicyLevelSchema),
  route: DownloadRouteSchema,
  requestedRoute: RequestedRouteSchema,
  quality: DownloadQualitySchema,
  status: DownloadStatusSchema,
  stage: nullable(DownloadStageSchema),
  progress: closed({ done: t.Integer({ minimum: 0 }), total: nullable(t.Integer({ minimum: 0 })) }),
  runner: DownloadRunnerSchema,
  transfer: nullable(
    closed({
      doneBytes: t.Integer({ minimum: 0 }),
      totalBytes: nullable(t.Integer({ minimum: 0 })),
      bytesPerSecond: t.Integer({ minimum: 0 }),
    }),
  ),
  estimatedGp: nullable(t.Integer({ minimum: 0 })),
  spentGp: nullable(t.Integer({ minimum: 0 })),
  pageCount: nullable(t.Integer({ minimum: 0 })),
  errorCode: nullable(DownloadErrorCodeSchema),
  errorMessage: nullable(t.String()),
  requestedBy: nullable(t.Integer()),
  origin: DownloadOrigin,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  finishedAt: nullable(IsoDateTime),
});
export type DownloadJobType = Static<typeof DownloadJob>;

export const downloadsRoutes = withIds({
  "downloads.create": route({
    module: "downloads",
    method: "POST",
    path: "/api/downloads",
    access: "user",
    summary: "다운로드 시작(이미 있으면 그 작업을 반환)",
    body: t.Object({
      gid: t.Integer({ minimum: 1 }),
      route: t.Optional(t.Literal("archive")),
      ack: t.Optional(
        t.Boolean({ description: "true = the user confirmed the warning screen (policy warn)" }),
      ),
    }),
    responses: {
      200: closed({ created: t.Literal(false), download: DownloadJob }),
      202: closed({ created: t.Literal(true), download: DownloadJob }),
    },
    errors: ["NOT_FOUND", "GALLERY_REMOVED", "POLICY_BLOCKED", "POLICY_ACK_REQUIRED", "RATE_LIMITED"],
    notes:
      "신규 요청은 Archive(리샘플)만 지원한다. 전체 예약·실행은 최대 9개이며 나머지는 DB 대기열에서 생성 순서대로 시작한다. 같은 갤러리의 Archive queued/running/done 작업이 있으면 created=false로 그 작업을 돌려준다. 원본 삭제 갤러리는 완료본 재사용만 허용하며 나머지는 GALLERY_REMOVED다. 차단 갤러리는 작업을 만들지 않는다.",
  }),
  "downloads.list": route({
    module: "downloads",
    method: "GET",
    path: "/api/downloads",
    access: "user",
    summary: "내 다운로드 작업, 최신순",
    query: t.Object({
      status: t.Optional(DownloadStatusSchema),
      category: t.Optional(ListParam("any of these gallery categories")),
      cursor: t.Optional(t.String({ minLength: 1, maxLength: 512 })),
      limit: t.Optional(LimitParam),
    }),
    responses: { 200: pageOf(DownloadJob) },
    errors: [],
  }),
  "downloads.byGallery": route({
    module: "downloads",
    method: "GET",
    path: "/api/downloads/by-gallery/:gid",
    access: "user",
    summary: "한 갤러리의 모든 작업(요청자 무관), 최신순",
    params: GidParams,
    responses: { 200: closed({ items: t.Array(DownloadJob) }) },
    errors: [],
  }),
  "downloads.summary": route({
    module: "downloads",
    method: "GET",
    path: "/api/downloads/summary",
    access: "user",
    summary: "내 대기·실행 개수와 전역 Archive 슬롯 사용량",
    responses: {
      200: closed({
        mine: closed({ queued: t.Integer({ minimum: 0 }), running: t.Integer({ minimum: 0 }) }),
        slots: closed({ running: t.Integer({ minimum: 0 }), limit: t.Integer({ minimum: 1 }) }),
        progress: nullable(closed({ done: t.Integer({ minimum: 0 }), total: t.Integer({ minimum: 1 }) })),
      }),
    },
    errors: [],
    notes:
      "mine은 요청 사용자의 download_requests로 연결된 작업만 센다. slots.running은 배정 판정과 같은 전역 Archive 점유 수다. progress는 내 실행 작업 중 전체 쪽수를 아는 것만 합산하며 없으면 null. 갤러리·사용자 정보는 싣지 않는다.",
  }),
  "downloads.get": route({
    module: "downloads",
    method: "GET",
    path: "/api/downloads/:id",
    access: "user",
    summary: "작업 하나(진행 상황은 이 라우트를 폴링)",
    params: t.Object({ id: Uuid }),
    responses: { 200: DownloadJob },
    errors: ["NOT_FOUND"],
  }),
});

// ======================================================================================
// reader  (/api/reader)
// ======================================================================================

export const readerRoutes = withIds({
  "reader.pages": route({
    module: "reader",
    method: "GET",
    path: "/api/reader/:gid/pages",
    access: "user",
    summary: "완료된 다운로드의 페이지 주소",
    params: GidParams,
    query: t.Object({ ack: t.Optional(BoolParam) }),
    responses: {
      200: closed({
        gid: t.Integer({ minimum: 1 }),
        route: DownloadRouteSchema,
        quality: DownloadQualitySchema,
        pageCount: t.Integer({ minimum: 0 }),
        pages: t.Array(
          closed({
            index: t.Integer({ minimum: 1 }),
            url: t.String(),
            width: nullable(t.Integer({ minimum: 1 })),
            height: nullable(t.Integer({ minimum: 1 })),
          }),
        ),
      }),
    },
    errors: ["NOT_FOUND", "POLICY_BLOCKED", "POLICY_ACK_REQUIRED", "DOWNLOAD_NOT_READY"],
    notes:
      "차단 갤러리는 파일이 있어도 거부한다. 경고 갤러리는 ack=true가 필요하다. 페이지 URL은 서명 쿠키로 보호되는 CloudFront URL이다.",
  }),
});

// ======================================================================================
// policy  (/api/admin/tag-policies)  -- admin only
// ======================================================================================

export const TagPolicyEntry = closed({
  tag: Tag,
  level: AssignablePolicyLevelSchema,
  fixed: t.Boolean({ description: "true = central DB hard block (U-15a); cannot be changed or removed" }),
  galleryCount: t.Integer({ minimum: 0 }),
  updatedAt: nullable(IsoDateTime),
  updatedBy: nullable(t.String()),
});

export const policyRoutes = withIds({
  "policy.list": route({
    module: "policy",
    method: "GET",
    path: "/api/admin/tag-policies",
    access: "admin",
    summary: "모든 태그 규칙, 고정 차단 먼저",
    responses: { 200: closed({ items: t.Array(TagPolicyEntry) }) },
    errors: [],
  }),
  "policy.addHard": route({
    module: "policy",
    method: "POST",
    path: "/api/admin/tag-policies/hard",
    access: "admin",
    summary: "중앙 하드 차단 규칙 추가 (수정·삭제 불가)",
    body: t.Object({ tag: Tag }),
    responses: { 200: TagPolicyEntry },
    errors: [],
  }),
  "policy.upsert": route({
    module: "policy",
    method: "PUT",
    path: "/api/admin/tag-policies",
    access: "admin",
    summary: "태그의 수준 설정",
    body: t.Object({ tag: Tag, level: AssignablePolicyLevelSchema }),
    responses: { 200: TagPolicyEntry },
    errors: ["POLICY_FIXED"],
    notes: "중앙 하드 규칙에 가려지는 태그는 수준과 무관하게 POLICY_FIXED.",
  }),
  "policy.remove": route({
    module: "policy",
    method: "DELETE",
    path: "/api/admin/tag-policies",
    access: "admin",
    summary: "태그 규칙 삭제",
    query: t.Object({ tag: Tag }),
    responses: { 204: Empty },
    errors: ["POLICY_FIXED", "NOT_FOUND"],
  }),
});

// ======================================================================================
// admin  (/api/admin/...)  -- users, access keys, sync, downloads
// ======================================================================================

export const AdminUser = closed({
  id: t.Integer(),
  username: t.String(),
  role: RoleSchema,
  discordId: nullable(t.String()),
  disabledAt: nullable(IsoDateTime),
  createdAt: IsoDateTime,
  lastLoginAt: nullable(IsoDateTime),
});

export const AccessKey = closed({
  id: t.Integer(),
  label: t.String(),
  prefix: t.String({ description: "first characters of the key, for recognition only" }),
  userId: t.Integer(),
  createdAt: IsoDateTime,
  lastUsedAt: nullable(IsoDateTime),
  revokedAt: nullable(IsoDateTime),
});

export const SyncRun = closed({
  id: t.Integer(),
  stream: SyncStreamSchema,
  startedAt: IsoDateTime,
  finishedAt: nullable(IsoDateTime),
  requests: t.Integer({ minimum: 0 }),
  newGids: t.Integer({ minimum: 0 }),
  knownGids: t.Integer({ minimum: 0 }),
  initialFetched: nullable(t.Integer({ minimum: 0 })),
  refreshed: nullable(t.Integer({ minimum: 0 })),
  failed: nullable(t.Integer({ minimum: 0 })),
  finishReason: nullable(SyncFinishReason),
  result: nullable(t.Union([t.Literal("ok"), t.Literal("partial"), t.Literal("banned"), t.Literal("error")])),
  verdict: nullable(VerdictSchema),
  note: nullable(t.String()),
});

export const SyncStream = closed({
  name: SyncStreamSchema,
  cursorGid: nullable(t.Integer()),
  highWaterGid: nullable(t.Integer()),
  lastSuccessAt: nullable(IsoDateTime),
  pausedAt: nullable(IsoDateTime),
  pausedReason: nullable(VerdictSchema),
  leaseExpiresAt: nullable(IsoDateTime),
  latestRun: nullable(SyncRun),
});

export const SyncMetadataCounts = closed({
  pendingMetaCount: t.Integer({ minimum: 0 }),
  countsAt: IsoDateTime,
  refreshBands: t.Array(
    closed({
      intervalHours: t.Union([t.Literal(3), t.Literal(6), t.Literal(24), t.Literal(72)]),
      targetCount: t.Integer({ minimum: 0 }),
      dueCount: t.Integer({ minimum: 0 }),
    }),
  ),
  extraRefreshDueCount: t.Integer({ minimum: 0 }),
});

export const AdminDownload = t.Composite(
  [
    DownloadJob,
    closed({
      requesterName: nullable(t.String()),
      priority: t.Integer({ minimum: 1, maximum: 3 }),
      ruleId: nullable(Uuid),
      ruleName: nullable(t.String()),
      discordGuildId: nullable(t.String()),
      discordUserId: nullable(t.String()),
    }),
  ],
  {
    additionalProperties: false,
  },
);

export const adminRoutes = withIds({
  "admin.listUsers": route({
    module: "admin",
    method: "GET",
    path: "/api/admin/users",
    access: "admin",
    summary: "모든 계정",
    query: CursorQuery,
    responses: { 200: pageOf(AdminUser) },
    errors: [],
  }),
  "admin.patchUser": route({
    module: "admin",
    method: "PATCH",
    path: "/api/admin/users/:id",
    access: "admin",
    summary: "역할 변경, 계정 정지·해제",
    params: t.Object({ id: IdParam }),
    body: t.Object({ role: t.Optional(RoleSchema), disabled: t.Optional(t.Boolean()) }),
    responses: { 200: AdminUser },
    errors: ["NOT_FOUND", "CONFLICT"],
    notes: "마지막 활성 관리자는 강등하거나 정지할 수 없다(CONFLICT).",
  }),
  "admin.listAccessKeys": route({
    module: "admin",
    method: "GET",
    path: "/api/admin/access-keys",
    access: "admin",
    summary: "모든 접근 키",
    responses: { 200: closed({ items: t.Array(AccessKey) }) },
    errors: [],
  }),
  "admin.createAccessKey": route({
    module: "admin",
    method: "POST",
    path: "/api/admin/access-keys",
    access: "admin",
    summary: "접근 키 생성(해당 guest 계정도 함께)",
    body: t.Object({ label: t.String({ minLength: 1, maxLength: 100 }) }),
    responses: {
      201: closed({
        accessKey: AccessKey,
        key: t.String({ description: "plain text, shown exactly once" }),
      }),
    },
    errors: [],
  }),
  "admin.revokeAccessKey": route({
    module: "admin",
    method: "DELETE",
    path: "/api/admin/access-keys/:id",
    access: "admin",
    summary: "접근 키 폐기, 그 세션 종료",
    notes: "마지막 활성 관리자의 접근 키는 폐기할 수 없다(CONFLICT).",
    params: t.Object({ id: IdParam }),
    responses: { 204: Empty },
    errors: ["NOT_FOUND", "CONFLICT"],
  }),
  "admin.syncStatus": route({
    module: "admin",
    method: "GET",
    path: "/api/admin/sync/status",
    access: "admin",
    summary: "집계를 기다리지 않는 수집 흐름과 최신 실행 상태",
    responses: { 200: closed({ items: t.Array(SyncStream), checkedAt: IsoDateTime }) },
    errors: [],
  }),
  "admin.syncStreams": route({
    module: "admin",
    method: "GET",
    path: "/api/admin/sync/streams",
    access: "admin",
    summary: "수집 흐름과 최신 실행, 초기 대기 및 최근 60일 구간별 집계(최대 60초 캐시)",
    responses: {
      200: t.Composite([SyncMetadataCounts, closed({ items: t.Array(SyncStream), checkedAt: IsoDateTime })], {
        additionalProperties: false,
      }),
    },
    errors: [],
  }),
  "admin.syncRuns": route({
    module: "admin",
    method: "GET",
    path: "/api/admin/sync/runs",
    access: "admin",
    summary: "최근 수집 실행 기록",
    query: t.Object({
      stream: t.Optional(SyncStreamSchema),
      cursor: t.Optional(t.String({ minLength: 1, maxLength: 512 })),
      limit: t.Optional(LimitParam),
    }),
    responses: { 200: pageOf(SyncRun) },
    errors: [],
  }),
  "admin.resumeStream": route({
    module: "admin",
    method: "POST",
    path: "/api/admin/sync/streams/:name/resume",
    access: "admin",
    summary: "흐름의 일시 정지 해제(차단·로그인 문제를 처리한 뒤)",
    params: t.Object({ name: SyncStreamSchema }),
    responses: { 200: SyncStream },
    errors: ["NOT_FOUND"],
  }),
  "admin.listDownloads": route({
    module: "admin",
    method: "GET",
    path: "/api/admin/downloads",
    access: "admin",
    summary: "모든 계정의 모든 다운로드 작업",
    query: t.Object({
      status: t.Optional(DownloadStatusSchema),
      origin: t.Optional(DownloadOrigin),
      ruleId: t.Optional(Uuid),
      cursor: t.Optional(t.String({ minLength: 1, maxLength: 512 })),
      limit: t.Optional(LimitParam),
    }),
    responses: { 200: pageOf(AdminDownload) },
    errors: [],
  }),
});

// ======================================================================================
// health (not a module: served by the app itself)
// ======================================================================================

export const HealthBody = closed({
  status: t.Union([t.Literal("ok"), t.Literal("degraded")]),
  db: t.Boolean(),
  time: IsoDateTime,
});
export type HealthBodyType = Static<typeof HealthBody>;
